import { motion, type HTMLMotionProps } from "framer-motion";
import { forwardRef, type ElementType, type ReactNode } from "react";
import {
  distance,
  duration,
  inViewOnce,
  revealVariants,
  stagger,
  staggerVariants,
  useReducedMotion,
} from "@/lib/motion";

type RevealAxis = "up" | "down" | "left" | "right" | "none";

interface RevealProps extends Omit<HTMLMotionProps<"div">, "variants" | "initial" | "animate"> {
  children: ReactNode;
  /** Direction the content travels in from. Defaults to a 12px rise. */
  axis?: RevealAxis;
  /** Travel distance in px (capped by the motion system at 20). */
  px?: number;
  /** Extra delay in seconds. Prefer `RevealGroup` for sequencing siblings. */
  delay?: number;
  seconds?: number;
  /** Render as a different element, e.g. `as="li"` inside a list. */
  as?: ElementType;
  /** Animate as part of a parent `RevealGroup` instead of its own viewport check. */
  inGroup?: boolean;
}

/**
 * Reveals a block of content as it enters the viewport.
 *
 * The element is always in the DOM and always readable: with reduced motion the
 * variants collapse to `opacity: 1` with no offset and no duration, so content
 * and CTAs never depend on the animation running.
 */
export const Reveal = forwardRef<HTMLDivElement, RevealProps>(
  ({ children, axis = "up", px = distance.md, delay = 0, seconds, as, inGroup, ...rest }, ref) => {
    const reduced = useReducedMotion();
    const Comp = (as ? (motion as never as Record<string, ElementType>)[as as string] : motion.div) ?? motion.div;
    const variants = revealVariants(reduced, { axis, px: Math.min(px, distance.lg), delay, seconds });

    if (inGroup) {
      return (
        <Comp ref={ref} variants={variants} {...rest}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        variants={variants}
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        {...rest}
      >
        {children}
      </Comp>
    );
  },
);

Reveal.displayName = "Reveal";

interface RevealGroupProps extends Omit<HTMLMotionProps<"div">, "variants" | "initial" | "animate"> {
  children: ReactNode;
  /** Seconds between each child. */
  step?: number;
  delay?: number;
  as?: ElementType;
}

/**
 * Staggers direct `Reveal inGroup` children so a section resolves in reading
 * order. Useful for card grids, capability lists, and lifecycle steps.
 */
export const RevealGroup = ({
  children,
  step = stagger.base,
  delay = 0,
  as,
  ...rest
}: RevealGroupProps) => {
  const reduced = useReducedMotion();
  const Comp = (as ? (motion as never as Record<string, ElementType>)[as as string] : motion.div) ?? motion.div;

  return (
    <Comp
      variants={staggerVariants(reduced, { step, delay })}
      initial="hidden"
      whileInView="visible"
      viewport={inViewOnce}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/**
 * Restrained hover feedback: a 2px lift and a border tint, nothing more.
 * Reduced motion keeps the border tint (a state cue) and drops the movement.
 */
export const HoverCardMotion = ({
  children,
  className,
  ...rest
}: HTMLMotionProps<"div"> & { children: ReactNode }) => {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      whileHover={reduced ? undefined : { y: -2 }}
      whileTap={reduced ? undefined : { y: 0 }}
      transition={{ duration: duration.instant, ease: [0.22, 0.61, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};
